import React, { useCallback, useEffect, useState } from 'react';
import MainLayout from '../../layouts/MainLayout';
import {
  Card,
  Table,
  Button,
  Space,
  message,
  Modal,
  Form,
  Input,
  Popconfirm,
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  DeleteOutlined,
  UnorderedListOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { callCenterQueuesApi, show422Error, ValidationError } from '../../utils/api';
import { TableSkeleton } from '../../components/SkeletonLoaders';
import PermissionGate from '../../components/PermissionGate';
import { PANEL_PRIMARY_BTN_CLASS } from '../../constants/ui';

const CallCenterQueues = () => {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm] = Form.useForm();
  const [addSubmitting, setAddSubmitting] = useState(false);
  const [deleteLoadingId, setDeleteLoadingId] = useState(null);
  const [editingId, setEditingId] = useState(null);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await callCenterQueuesApi.getAll();
      const payload = res?.data ?? [];
      setRows(Array.isArray(payload) ? payload : payload.data ?? []);
    } catch (error) {
      console.error('خطا در دریافت صف‌ها:', error);
      message.error(error?.message || 'خطا در دریافت صف‌ها');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const openAddModal = () => {
    setEditingId(null);
    setAddOpen(true);
    addForm.resetFields();
  };

  const openEditModal = (record) => {
    setEditingId(record.id);
    setAddOpen(true);
    addForm.setFieldsValue({
      name: record.name,
      title: record.title,
    });
  };

  const submitAdd = async () => {
    try {
      const values = await addForm.validateFields();
      setAddSubmitting(true);
      if (editingId) {
        await callCenterQueuesApi.update(editingId, {
          name: values.name,
          title: values.title,
        });
        message.success('صف با موفقیت ویرایش شد');
      } else {
        await callCenterQueuesApi.create({
          name: values.name,
          title: values.title,
        });
        message.success('صف با موفقیت اضافه شد');
      }
      setAddOpen(false);
      await fetchRows();
    } catch (error) {
      if (error?.errorFields) return;
      console.error('خطا در ذخیره صف:', error);
      if (error instanceof ValidationError) {
        show422Error(error, addForm);
      } else {
        message.error(error?.message || 'خطا در ذخیره صف');
      }
    } finally {
      setAddSubmitting(false);
    }
  };

  const deleteRow = async (row) => {
    setDeleteLoadingId(row.id);
    try {
      await callCenterQueuesApi.delete(row.id);
      message.success('صف حذف شد');
      await fetchRows();
    } catch (error) {
      console.error('خطا در حذف صف:', error);
      message.error(error?.message || 'خطا در حذف صف');
    } finally {
      setDeleteLoadingId(null);
    }
  };

  const columns = [
    {
      title: 'شماره/شناسه صف (Name)',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'عنوان نمایشی (Title)',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: 'عملیات',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space>
          <PermissionGate module="callcenter" permission="manage_callcenter_queues">
            <Button
              type="text"
              icon={<EditOutlined className="text-blue-500" />}
              onClick={() => openEditModal(record)}
            />
            <Popconfirm
              title="آیا از حذف این صف مطمئن هستید؟"
              onConfirm={() => deleteRow(record)}
              okText="بله"
              cancelText="خیر"
              placement="topRight"
            >
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                loading={deleteLoadingId === record.id}
                disabled={deleteLoadingId && deleteLoadingId !== record.id}
              />
            </Popconfirm>
          </PermissionGate>
        </Space>
      ),
    },
  ];

  return (
    <MainLayout
      title="مدیریت صف‌ها (Queues)"
      activeMenu="/call-center/queues"
      breadcrumbs={[
        { title: 'مرکز تماس', path: '/call-center/calls' },
        { title: 'صف‌ها' },
      ]}
    >
      <div className="w-full">
        <PermissionGate module="callcenter" permission="manage_callcenter_queues" showWarning>
          <Card
            bordered={false}
            className="shadow-sm rounded-lg"
            styles={{ body: { padding: '1.5rem' } }}
            title={
              <div className="flex items-center gap-2">
                <UnorderedListOutlined className="text-gray-500" />
                <span className="font-semibold text-gray-800">لیست صف‌های سیستم</span>
              </div>
            }
            extra={
              <Space>
                <Button icon={<ReloadOutlined />} onClick={() => fetchRows()}>
                  به‌روزرسانی
                </Button>
                <Button
                  type="primary"
                  className={PANEL_PRIMARY_BTN_CLASS}
                  icon={<PlusOutlined />}
                  onClick={openAddModal}
                >
                  افزودن صف
                </Button>
              </Space>
            }
          >
            {loading ? (
              <TableSkeleton columns={3} rows={5} />
            ) : (
              <Table
                columns={columns}
                dataSource={rows}
                rowKey="id"
                pagination={false}
                size="middle"
              />
            )}
          </Card>
        </PermissionGate>
      </div>

      <Modal
        title={editingId ? 'ویرایش صف' : 'افزودن صف جدید'}
        open={addOpen}
        onCancel={() => setAddOpen(false)}
        onOk={submitAdd}
        confirmLoading={addSubmitting}
        okText="ذخیره"
        cancelText="انصراف"
        okButtonProps={{ className: PANEL_PRIMARY_BTN_CLASS }}
      >
        <Form form={addForm} layout="vertical" className="mt-4">
          <Form.Item
            name="name"
            label="شناسه صف (در سیستم تلفنی)"
            rules={[{ required: true, message: 'لطفاً شناسه صف را وارد کنید' }]}
            help="مثلاً 900، باید دقیقاً با سیستم تلفنی مطابقت داشته باشد."
          >
            <Input placeholder="مثال: 900" dir="ltr" />
          </Form.Item>
          <Form.Item
            name="title"
            label="عنوان نمایشی"
            rules={[{ required: true, message: 'لطفاً عنوان نمایشی را وارد کنید' }]}
            help="مثال: واحد فروش، پشتیبانی و..."
          >
            <Input placeholder="مثال: واحد فروش" />
          </Form.Item>
        </Form>
      </Modal>
    </MainLayout>
  );
};

export default CallCenterQueues;
